'use client';
import React, { useState } from 'react';
import Badge from '@/components/ui/Badge';
import Icon from '@/components/ui/AppIcon';

type CalvingStatus = 'Confirmed' | 'Monitoring' | 'Predicted' | 'Historical';

interface CalvingEvent {
  id: string;
  region: string;
  date: string;
  massKm3: number;
  areaKm2: number;
  integrity_before: number;
  integrity_after: number;
  trigger: string;
  satellite: boolean;
  sonar: boolean;
  status: CalvingStatus;
}

const events: CalvingEvent[] = [
  { id: 'calv-0041', region: 'Thwaites', date: '2026-06-14', massKm3: 12.4, areaKm2: 840, integrity_before: 34.2, integrity_after: 31.4, trigger: 'Basal melt cavity collapse', satellite: true, sonar: true, status: 'Confirmed' },
  { id: 'calv-0040', region: 'Thwaites', date: '2026-06-08', massKm3: 8.1, areaKm2: 560, integrity_before: 35.8, integrity_after: 34.2, trigger: 'Rift propagation — NW sector', satellite: true, sonar: true, status: 'Confirmed' },
  { id: 'calv-0039', region: 'Pine Island', date: '2026-06-11', massKm3: 6.3, areaKm2: 420, integrity_before: 47.2, integrity_after: 44.8, trigger: 'Warm CDW intrusion', satellite: true, sonar: false, status: 'Confirmed' },
  { id: 'calv-0038', region: 'Thwaites', date: '2026-06-17', massKm3: 4.2, areaKm2: 290, integrity_before: 31.4, integrity_after: 29.8, trigger: 'Grounding line retreat predicted', satellite: false, sonar: true, status: 'Predicted' },
  { id: 'calv-0037', region: 'Pine Island', date: '2026-05-29', massKm3: 3.8, areaKm2: 255, integrity_before: 48.9, integrity_after: 47.2, trigger: 'Tidal flexure fatigue', satellite: true, sonar: true, status: 'Confirmed' },
  { id: 'calv-0036', region: 'Larsen C', date: '2026-05-18', massKm3: 2.1, areaKm2: 140, integrity_before: 63.4, integrity_after: 62.1, trigger: 'Surface meltwater ponding', satellite: true, sonar: false, status: 'Confirmed' },
  { id: 'calv-0035', region: 'Thwaites', date: '2026-06-14', massKm3: 18.7, areaKm2: 1240, integrity_before: 36.4, integrity_after: 32.1, trigger: 'Monitoring — rift widening 2.4m/day', satellite: true, sonar: true, status: 'Monitoring' },
  { id: 'calv-0034', region: 'Larsen C', date: '2026-04-22', massKm3: 1.4, areaKm2: 95, integrity_before: 63.8, integrity_after: 63.4, trigger: 'Ice tongue shear', satellite: true, sonar: false, status: 'Historical' },
  { id: 'calv-0033', region: 'Pine Island', date: '2026-04-07', massKm3: 5.2, areaKm2: 350, integrity_before: 50.1, integrity_after: 48.9, trigger: 'Subglacial discharge pulse', satellite: true, sonar: true, status: 'Historical' },
  { id: 'calv-0032', region: 'Thwaites', date: '2026-03-15', massKm3: 9.6, areaKm2: 640, integrity_before: 38.7, integrity_after: 36.4, trigger: 'Bed topography step — Retrograde slope', satellite: true, sonar: true, status: 'Historical' },
];

const statusBadge: Record<CalvingStatus, 'negative' | 'warning' | 'info' | 'neutral'> = {
  Confirmed: 'negative',
  Monitoring: 'warning',
  Predicted: 'info',
  Historical: 'neutral',
};

export default function CalvingEventLog() {
  const [filter, setFilter] = useState<CalvingStatus | 'all'>('all');

  const filtered = filter === 'all' ? events : events.filter(e => e.status === filter);

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-sm font-semibold text-foreground">Calving Event Log</h2>
          <p className="text-xs text-muted-foreground">Sonar + satellite confirmed ice detachment events</p>
        </div>
        <div className="flex items-center gap-1.5">
          {(['all', 'Confirmed', 'Monitoring', 'Predicted', 'Historical'] as const).map((f) => (
            <button
              key={`calv-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all duration-150 ${
                filter === f
                  ? 'bg-primary/10 text-primary border border-primary/20' :'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              {f === 'all' ? 'All' : f}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto scrollbar-ocean">
        <table className="w-full text-xs min-w-[700px]">
          <thead>
            <tr className="border-b border-border">
              {['Event ID', 'Region', 'Date', 'Mass (km³)', 'Area (km²)', 'Integrity Δ', 'Trigger', 'Sources', 'Status'].map((col) => (
                <th key={`th-calv-${col}`} className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-widest text-muted-foreground whitespace-nowrap">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((ev) => (
              <tr key={ev.id} className="border-b border-border/50 hover:bg-muted/40 transition-colors duration-100">
                <td className="px-4 py-3 font-mono text-primary text-[11px]">{ev.id}</td>
                <td className="px-4 py-3 font-semibold text-foreground">{ev.region}</td>
                <td className="px-4 py-3 font-mono text-muted-foreground whitespace-nowrap">{ev.date}</td>
                <td className="px-4 py-3 font-tabular text-negative font-semibold">{ev.massKm3.toFixed(1)}</td>
                <td className="px-4 py-3 font-tabular text-muted-foreground">{ev.areaKm2.toLocaleString()}</td>
                <td className="px-4 py-3">
                  <span className="font-tabular text-negative text-[11px] font-semibold">
                    {ev.integrity_before.toFixed(1)} → {ev.integrity_after.toFixed(1)}
                  </span>
                </td>
                <td className="px-4 py-3 text-muted-foreground max-w-[200px] truncate" title={ev.trigger}>{ev.trigger}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    {ev.satellite && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-accent/10 text-accent border border-accent/20 font-semibold">SAT</span>
                    )}
                    {ev.sonar && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20 font-semibold">SON</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <Badge variant={statusBadge[ev.status]} dot>{ev.status}</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="px-5 py-3 border-t border-border flex items-center justify-between">
        <p className="text-xs text-muted-foreground">Showing {filtered.length} of {events.length} events</p>
        <button className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors">
          <Icon name="ArrowDownTrayIcon" size={13} />
          Export to CSV
        </button>
      </div>
    </div>
  );
}